import { useMemo } from 'react';
import type { BlockContent, BlockStyle } from '@/blocks/domain/types';
import type { TemplateBlockInstance, TemplateVersion } from '@/templates/domain/types';
import { DocumentRender } from '@/document-render/presentation/DocumentRender';
import { applySampleDataSwitch } from '@/document-render/application/applySampleDataSwitch';
import { usePreviewContext } from '@/document-render/application/usePreviewContext';
import { useKnownVariablePaths } from '@/document-render/application/useKnownVariablePaths';
import type { DocumentTypeRef } from '@/document-types/domain/types/DocumentType';
import { QUOTE_DOCUMENT_TYPE_REF } from '@/document-types/domain/constants/quoteDocumentType';

interface Props {
  draft: { content: BlockContent; style: BlockStyle } | null;
  accentColor?: string;
  documentTypeRef?: DocumentTypeRef;
  /**
   * The canvas toolbar's "Sample data" toggle (LLAMS-610 Part 8, `CanvasToolbar`'s own doc
   * comment; one switch, `applySampleDataSwitch`): `true` fills `{{variable}}` tokens with the
   * sample context's values, `false` renders the EMPTY context so every kind shows its token
   * placeholder / empty state. Omit to leave `usePreviewContext`'s own default
   * (`'unbound'` for the Block Library standalone context) untouched — every existing caller keeps
   * today's behaviour.
   */
  sampleData?: boolean;
}

export function LivePreview({ draft, accentColor, documentTypeRef = QUOTE_DOCUMENT_TYPE_REF, sampleData }: Props) {
  const { context: previewContext, source } = usePreviewContext(documentTypeRef);
  const knownPaths = useKnownVariablePaths(documentTypeRef);
  // Memoized so `context` keeps ONE stable reference across renders that don't actually change
  // anything relevant — `useBlockHeightMeasurement`'s off-screen measurement pass (`measureBlockHeights.tsx`)
  // depends on `context` and remounts its whole tree whenever the reference changes, so a fresh
  // `{ ...previewContext, … }` object on every render (e.g. every keystroke elsewhere in the editor)
  // would otherwise reflow the canvas continuously — exactly the instability a real-browser E2E run
  // surfaced against the "insert a dynamic value" button.
  const context = useMemo(() => {
    if (sampleData === undefined) return previewContext;
    return applySampleDataSwitch(previewContext, source, sampleData);
  }, [previewContext, source, sampleData]);

  // Memoized on `draft` alone (not recreated on every render): `DocumentRender`'s own
  // `renderableInstances` memo keys off `templateVersion.composition`'s reference, so a fresh
  // `[instance]` array here on every render — regardless of whether `draft` actually changed —
  // would invalidate it (and the off-screen pagination measurement pass under it) continuously.
  // `editorDraft.draft` (`useBlockEditorDraft`) is real React state, so this reference is already
  // stable whenever nothing the author did changed the content/style.
  const templateVersion = useMemo<TemplateVersion | null>(() => {
    if (!draft) return null;

    const instance: TemplateBlockInstance = {
      templateBlockInstanceId: 'preview-1',
      templateVersionId: 'preview-tv-1',
      sourceBlockId: null,
      sourceBlockVersionId: null,
      importedBlockJson: draft,
      placement: { page: 1, x: 0, y: 0, zIndex: 0 },
      visible: true,
      hideForCustomerTags: [],
      sortOrder: 0
    };

    return {
      templateVersionId: instance.templateVersionId,
      templateId: 'preview-template-1',
      versionNumber: 1,
      pdfmeTemplateJson: null,
      composition: [instance],
      changeNote: null,
      publishedAt: null,
      createdAt: new Date().toISOString()
    };
  }, [draft]);

  if (!draft || !templateVersion) return <pre>{JSON.stringify(draft, null, 2)}</pre>;

  return <DocumentRender templateVersion={templateVersion} context={context} knownPaths={knownPaths} accentColor={accentColor} />;
}
